import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  Select,
  Spin,
  Icon,
  Checkbox,
  Popconfirm,
  message
} from 'antd';
import { get, del } from '../../../utils/httpRequests';
import { APIs } from '../../../utils/apis';
import moment from 'moment';

const antIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />;
const columns = [
  {
    title: 'Email',
    width: 20
  },
  {
    title: 'Access Granted',
    width: 30
  },
  {
    title: 'Access Type',
    width: 12
  },
  {
    title: 'Created At',
    width: 15
  },
  {
    title: 'Updated At',
    width: 15
  },
  {
    title: 'Edit',
    width: 20
  }
];

const { Option } = Select;

const List = () => {
  const [state, setState] = useState({
    list: [],
    isFetching: true,
    allChecked: false,
    user: {}
  });

  const [enterprises, setEnterprises] = useState({
    list: [],
    isFetching: true,
    enterprise: {}
  });

  const fetchUsers = emailid => {
    get({
      url: APIs.USER.ACCESS + (emailid ? `?emailid=${emailid}` : '')
    }).then(response => {
      if (!response) {
        throw Error(response.statusText);
      }
      setState({
        ...state,
        isFetching: false,
        list: response.data.result.list,
        ...{ user: emailid ? state.user : {} }
      });
    });
  };

  const fetch = () => {
    get({
      url: APIs.ACTIVE_REPORTS.META_DATA + '&filters=enterprise'
    }).then(response => {
      if (!response) {
        throw Error(response.statusText);
      }
      const sortedEnterprises =
        response.data.result.enterprises.sort((a, b) =>
          a?.name?.localeCompare(b.name)
        ) || [];
      setEnterprises({
        ...enterprises,
        list: sortedEnterprises,
        isFetching: false
      });
    });
  };

  useEffect(() => {
    fetchUsers();
    fetch();
  }, []);

  const reset = () => {
    setState({ ...state, user: {} });
    fetchUsers();
  };

  const enterpriseNameByIds = ids =>
    enterprises.list
      .filter(enterprise => ids.includes(enterprise.id))
      .map(enterprise => enterprise.name)
      .join(', ');

  const handleChange = index => {
    setState({ ...state, user: state.list[index] });
  };

  const handleSearch = () => {
    fetchUsers(state.user.emailid);
  };

  const onChangeAll = e => {
    const list = state.list.map(item => {
      item.checked = e.target.checked;
      return item;
    });
    setState({ ...state, list, allChecked: e.target.checked });
  };

  const onChange = index => {
    state.list[index].checked = !state.list[index].checked;
    setState({ ...state });
    const allChecked = state.list.every(enterprise => enterprise.checked);
    setState({ ...state, allChecked });
  };

  const onDelete = async () => {
    const list = state.list.filter(item => item.checked);
    if (list.length === 0) {
      message.warning('No items selected for deletion');
      return;
    }

    try {
      // Create an array of delete requests
      const deletePromises = list.map(item => {
        return del({
          url: `${APIs.USER.ACCESS}${item.id}` // Assuming each item has an 'id' field
        });
      });

      // Wait for all delete requests to complete
      const responses = await Promise.all(deletePromises);

      // Check if all deletes were successful
      const allSuccess = responses.every(
        response => response.data.meta.status === 200
      );

      if (allSuccess) {
        message.success('All selected items were successfully deleted');
        fetchUsers(); // Refresh the list after successful deletion
        fetch(); // Refresh the list after successful deletion
      } else {
        message.error('One or more items could not be deleted');
      }
    } catch (error) {
      // Handle any errors that occurred during the delete requests
      message.error('An error occurred while deleting items');
    }
  };

  const cancel = () => {
    message.info('Canceled');
  };

  return (
    <div>
      <Row className="margin-bottom-10 view-header">
        <Col span={14}>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select User"
            optionFilterProp="children"
            value={state.user?.emailid ?? undefined}
            onChange={handleChange}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {state.list.map((user, i) => (
              <Option key={i} value={i}>
                {user.emailid}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={10} className="text-right">
          <Button
            className="ant-btn ant-btn-primary margin-right-10"
            disabled={!state.user?.emailid}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button
            className="ant-btn ant-btn-primary margin-right-10"
            onClick={reset}
          >
            Reset
          </Button>
          <NavLink
            to={`/dashboard/access-control/create`}
            className="ant-btn ant-btn-primary"
          >
            Grant Access to New User
          </NavLink>
        </Col>
      </Row>
      <Row>
        {state.list.length !== 0 ? (
          <Col span={24} className="margin-bottom-10">
            <Popconfirm
              title="Are you sure want to delete selected alerts?"
              onConfirm={onDelete}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
              disabled={state.list.every(item => !item.checked)}
            >
              <Button
                className={`ant-btn ant-btn-primary margin-right-10 ${state.list.every(
                  item => !item.checked
                ) && 'disabled'}`}
              >
                Delete Selected
              </Button>
            </Popconfirm>
          </Col>
        ) : null}
        <Col span={24} className="margin-bottom-10">
          {state.isFetching || enterprises.isFetching ? (
            <Spin indicator={antIcon} />
          ) : (
            <>
              {state.list.length <= 0 ? (
                <div>&nbsp; No Data</div>
              ) : (
                <>
                  <table className={`ant-table ant-table-small width-100pt`}>
                    <thead className="ant-table-thead">
                      <tr>
                        <th className="padding-8">
                          <Checkbox
                            onChange={onChangeAll}
                            checked={state.allChecked}
                          ></Checkbox>
                        </th>
                        {columns.map((item, i) => (
                          <th
                            className="padding-8"
                            key={i}
                            width={`${item.width}%`}
                          >
                            {item.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                      {state.list.map((item, index) => (
                        <tr key={index}>
                          <td className="padding-8">
                            <Checkbox
                              onChange={() => onChange(index)}
                              checked={item.checked}
                            ></Checkbox>
                          </td>
                          <td className="padding-8">{item.emailid}</td>
                          <td className="padding-8">
                            {item.accessToAll
                              ? 'All Enterprises'
                              : item.enterpriseIds.length === 0
                              ? 'None'
                              : enterpriseNameByIds(item.enterpriseIds)}
                          </td>
                          <td className="padding-8">
                            {item.userAccessType === 'restricted'
                              ? 'Restricted'
                              : 'Admin'}
                          </td>
                          <td className="padding-8">
                            {moment(item.createdAt).format(
                              'DD-MMM-YYYY hh:mm a'
                            )}
                          </td>
                          <td className="padding-8">
                            {moment(item.updatedAt).format(
                              'DD-MMM-YYYY hh:mm a'
                            )}
                          </td>
                          <td className="padding-8">
                            <NavLink
                              to={`/dashboard/access-control/edit/${item.id}`}
                            >
                              Edit Detail
                            </NavLink>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default List;
